import React, { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LabelList, Cell, PieChart, Pie, Legend } from 'recharts';
import ResponseDrillTable, { toDrillRows } from './ResponseDrillTable';

// Which bar an answer is counted under. Values that do not match an authored
// option overflow into the visible "Other" bucket, so the drill-down finds them
// there instead of losing them.
export const bucketOf = (value, baseCategories = []) => (baseCategories.includes(value) ? value : 'Other');

const COLORS = ['#5B9FFF', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];
const BAR_COLOR = '#5B9FFF';
const OTHER_COLOR = '#9AA0A6';
const MEDIAN_COLOR = '#2B6BBF';

// A rating scale ("1 to 10") is a set member by response type, but its members
// are numbers and the author needs the same description a numeric question
// gets. Numeric summaries are emitted only when EVERY observed member is a
// number: an average over "Agree"/"Disagree" would be an invented statistic.
const asNumber = (value) => {
  if (value === null || value === undefined || value === '') return NaN;
  return typeof value === 'number' ? value : Number(String(value).trim());
};
export const allNumeric = (list) => list.length > 0 && list.every(v => Number.isFinite(asNumber(v)));

// Weighted over the counts, so the description is of the answers rather than
// of the distinct members.
export function numericSummary(data) {
  const observations = [];
  data.forEach(({ category, count }) => {
    const value = asNumber(category);
    for (let i = 0; i < count; i += 1) observations.push(value);
  });
  if (!observations.length) return null;
  observations.sort((a, b) => a - b);
  const n = observations.length;
  const mean = observations.reduce((sum, v) => sum + v, 0) / n;
  const mid = Math.floor(n / 2);
  const median = n % 2 === 0 ? (observations[mid - 1] + observations[mid]) / 2 : observations[mid];
  // Population σ, matching ScalarCanvas so the two surfaces describe the same
  // spread the same way.
  const sd = Math.sqrt(observations.reduce((acc, v) => acc + (v - mean) ** 2, 0) / n);
  return { n, mean, median, sd, min: observations[0], max: observations[n - 1] };
}

const formatNumber = (value) => (Number.isInteger(value) ? value : value.toFixed(2));

export default function SetMemberCanvas({
  values = [],
  options = [],
  isOrdinal = false,
  view = 'bar',
  showProportion = false,
  rows = [],
  onOpenSession
}) {
  // Which category the author clicked. Held above the empty-state return so the
  // hook order never depends on whether answers arrived.
  const [drillCategory, setDrillCategory] = useState(null);
  const canDrill = rows.length > 0;

  if (values.length === 0) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '200px',
        color: '#5F6368',
        fontFamily: 'var(--font-family-primary)'
      }}>
        No responses yet.
      </div>
    );
  }

  // Nothing-disappears: every value is counted. Values that do not match an
  // authored option overflow into a visible "Other" bucket (casing /
  // localization mismatches must not silently zero a bar). Base counts + Other
  // always sum to values.length, so proportions reconcile.
  // Authored options keep their authored order. Derived members are sorted
  // numerically when they are all numbers — a plain sort puts "10" before "2",
  // which silently misorders every rating scale and its median with it.
  const observed = [...new Set(values)];
  const baseCategories = options.length
    ? options
    : (allNumeric(observed)
        ? observed.sort((a, b) => Number(a) - Number(b))
        : observed.sort());
  const counts = {};
  let otherCount = 0;

  baseCategories.forEach(cat => {
    counts[cat] = 0;
  });

  values.forEach(value => {
    if (baseCategories.includes(value)) {
      counts[value] += 1;
    } else {
      otherCount += 1;
    }
  });

  const total = values.length;
  const data = baseCategories.map((category) => {
    const count = counts[category];
    return {
      category,
      count,
      proportion: total ? (count / total) * 100 : 0
    };
  });

  if (otherCount > 0) {
    data.push({
      category: 'Other',
      count: otherCount,
      proportion: (otherCount / total) * 100
    });
  }

  const drillRows = drillCategory == null
    ? []
    : toDrillRows(rows.filter(row => bucketOf(row.value, baseCategories) === drillCategory));

  // A second click on the bucket already open closes it, so a bar is a toggle
  // rather than a trap the author has to hunt for the × to escape.
  const handleSliceClick = (entry) => {
    if (!canDrill) return;
    const category = entry?.category ?? entry?.payload?.category ?? null;
    setDrillCategory(current => (current === category ? null : category));
  };

  // The strip describes the authored members only; "Other" is off-scale and
  // carries no numeric value, so it cannot enter a mean or a range.
  const scored = data.filter(row => row.category !== 'Other');
  const summary = allNumeric(scored.map(row => row.category)) ? numericSummary(scored) : null;
  const statItems = summary && [
    { label: 'n', value: summary.n },
    { label: 'mean', value: formatNumber(summary.mean) },
    { label: 'median', value: formatNumber(summary.median) },
    { label: 'σ', value: formatNumber(summary.sd) },
    { label: 'min', value: formatNumber(summary.min) },
    { label: 'max', value: formatNumber(summary.max) },
  ];

  const renderSummaryStrip = () => {
    if (!statItems) return null;
    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginBottom: 16 }}>
        {statItems.map(item => (
          <div key={item.label} style={{ display: 'flex', flexDirection: 'column' }}>
            <span style={{ fontSize: 11, color: '#5F6368' }}>{item.label}</span>
            <span style={{ fontSize: 13, color: '#202124', fontWeight: 600 }}>{item.value}</span>
          </div>
        ))}
        {otherCount > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <span style={{ fontSize: 11, color: '#5F6368' }}>other</span>
            <span style={{ fontSize: 13, color: '#202124', fontWeight: 600 }}>{otherCount}</span>
          </div>
        )}
      </div>
    );
  };

  const renderBarChart = () => {
    // Median is computed over the AUTHORED categories only — "Other" is
    // off-scale and must not shift the ordinal median.
    let medianCategory = null;
    if (isOrdinal) {
      const baseTotal = baseCategories.reduce((sum, cat) => sum + counts[cat], 0);
      if (baseTotal > 0) {
        let cumulative = 0;
        const target = baseTotal / 2;
        for (const cat of baseCategories) {
          cumulative += counts[cat];
          if (cumulative >= target) {
            medianCategory = cat;
            break;
          }
        }
      }
    }

    return (
      <div>
        <ResponsiveContainer width="100%" height={320}>
          <BarChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
            <XAxis
              dataKey="category"
              tick={{ fill: '#5F6368', fontSize: 12 }}
              axisLine={{ stroke: '#E8EAED' }}
              tickLine={{ stroke: '#E8EAED' }}
            />
            <YAxis
              tick={{ fill: '#5F6368', fontSize: 12 }}
              axisLine={{ stroke: '#E8EAED' }}
              tickLine={{ stroke: '#E8EAED' }}
            />
            <Tooltip
              contentStyle={{ fontFamily: 'var(--font-family-primary)' }}
              formatter={(value, name) => [showProportion ? `${value.toFixed(1)}%` : value, name]}
            />
            <Bar dataKey={showProportion ? 'proportion' : 'count'} onClick={handleSliceClick} cursor={canDrill ? 'pointer' : undefined}>
              {data.map((entry, index) => {
                let fill = BAR_COLOR;
                if (entry.category === 'Other') {
                  fill = OTHER_COLOR;
                } else if (entry.category === medianCategory) {
                  fill = MEDIAN_COLOR;
                }
                return <Cell key={`cell-${index}`} fill={fill} />;
              })}
              <LabelList
                dataKey={showProportion ? 'proportion' : 'count'}
                position="top"
                formatter={value => showProportion ? `${value.toFixed(1)}%` : value}
                style={{ fill: '#202124', fontSize: 12, fontFamily: 'var(--font-family-primary)' }}
              />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
        {/* The strip already states the median exactly for numeric members;
            repeating the median CATEGORY beside it would show two different
            numbers for one word. */}
        {medianCategory && !summary && (
          <div style={{ textAlign: 'center', color: '#5F6368', marginTop: '8px', fontFamily: 'var(--font-family-primary)' }}>
            Median: {medianCategory}
          </div>
        )}
      </div>
    );
  };

  const renderPieChart = () => {
    // A slice too thin to hold text gets no label — its share is still exact
    // in the legend and the tooltip, and crowding the rim with overlapping
    // text is what made the chart unreadable.
    const LABEL_FLOOR_PERCENT = 3;
    const sliceLabel = ({ category, count, proportion, percent, x, y, textAnchor }) => {
      if (percent * 100 < LABEL_FLOOR_PERCENT) return null;
      return (
        <text
          x={x}
          y={y}
          textAnchor={textAnchor}
          dominantBaseline="central"
          style={{ fontSize: 12, fontFamily: 'var(--font-family-primary)' }}
        >
          <tspan fill="#202124" fontWeight="600">{category}</tspan>
          <tspan fill="#5F6368"> {showProportion ? `${proportion.toFixed(1)}%` : `${count} · ${proportion.toFixed(1)}%`}</tspan>
        </text>
      );
    };

    return (
      <ResponsiveContainer width="100%" height={460}>
        <PieChart margin={{ top: 16, right: 96, bottom: 16, left: 96 }}>
          <Pie
            data={data}
            dataKey={showProportion ? 'proportion' : 'count'}
            nameKey="category"
            cx="50%"
            cy="50%"
            outerRadius="78%"
            minAngle={2}
            paddingAngle={1}
            isAnimationActive={false}
            labelLine={{ stroke: '#DADCE0' }}
            label={sliceLabel}
            onClick={handleSliceClick}
            cursor={canDrill ? 'pointer' : undefined}
          >
            {data.map((entry, index) => {
              const fill = entry.category === 'Other' ? OTHER_COLOR : COLORS[index % COLORS.length];
              return <Cell key={`cell-${index}`} fill={fill} stroke="#FFFFFF" strokeWidth={1} />;
            })}
          </Pie>
          <Tooltip
            contentStyle={{ fontFamily: 'var(--font-family-primary)' }}
            formatter={(value, name, entry) => [
              `${entry?.payload?.count} of ${total} · ${entry?.payload?.proportion.toFixed(1)}%`,
              name,
            ]}
          />
          <Legend
            verticalAlign="bottom"
            height={36}
            formatter={(name, entry) => (
              <span style={{ fontSize: 12, color: '#5F6368', fontFamily: 'var(--font-family-primary)' }}>
                {name} ({entry?.payload?.count})
              </span>
            )}
          />
        </PieChart>
      </ResponsiveContainer>
    );
  };

  const renderTable = () => {
    const totalRow = data.reduce((acc, row) => {
      acc.count += row.count;
      acc.proportion += row.proportion;
      return acc;
    }, { count: 0, proportion: 0 });

    return (
      <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'var(--font-family-primary)' }}>
        <thead>
          <tr>
            <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #E8EAED', color: '#202124' }}>Category</th>
            <th style={{ textAlign: 'right', padding: '8px', borderBottom: '1px solid #E8EAED', color: '#202124' }}>Count</th>
            <th style={{ textAlign: 'right', padding: '8px', borderBottom: '1px solid #E8EAED', color: '#202124' }}>Proportion (%)</th>
          </tr>
        </thead>
        <tbody>
          {data.map((row, index) => (
            <tr key={index}>
              <td style={{ padding: '8px', borderBottom: '1px solid #E8EAED', color: '#202124' }}>{row.category}</td>
              <td style={{ padding: '8px', borderBottom: '1px solid #E8EAED', color: '#202124', textAlign: 'right' }}>{row.count}</td>
              <td style={{ padding: '8px', borderBottom: '1px solid #E8EAED', color: '#202124', textAlign: 'right' }}>{row.proportion.toFixed(1)}%</td>
            </tr>
          ))}
          <tr style={{ fontWeight: 'bold' }}>
            <td style={{ padding: '8px', borderTop: '2px solid #E8EAED', color: '#202124' }}>Total</td>
            <td style={{ padding: '8px', borderTop: '2px solid #E8EAED', color: '#202124', textAlign: 'right' }}>{totalRow.count}</td>
            <td style={{ padding: '8px', borderTop: '2px solid #E8EAED', color: '#202124', textAlign: 'right' }}>{totalRow.proportion.toFixed(1)}%</td>
          </tr>
        </tbody>
      </table>
    );
  };

  const renderContent = () => {
    switch (view) {
      case 'bar': return renderBarChart();
      case 'pie': return renderPieChart();
      case 'table': return renderTable();
      default: return renderBarChart();
    }
  };

  return (
    <div style={{ fontFamily: 'var(--font-family-primary)' }}>
      {renderSummaryStrip()}
      {renderContent()}
      {canDrill && view !== 'table' && (
        <div style={{ fontSize: 12, color: '#5F6368', marginTop: 8 }}>
          Click a {view === 'pie' ? 'slice' : 'bar'} to see its answers.
        </div>
      )}
      {drillCategory != null && (
        <ResponseDrillTable
          label={drillCategory}
          rows={drillRows}
          onClose={() => setDrillCategory(null)}
          onOpenSession={onOpenSession}
        />
      )}
    </div>
  );
}
